import { useParams } from 'react-router-dom';
import Back from 'components/Back';
import ReportDetails from 'components/SummaryReport/ReportDetails';
import ProgressIndicator from 'components/ProgressIndicator';
import SummaryReportPageHeader from 'components/SummaryReportPageHeader';
import {
  getIterationById,
  getIterationDetailsById,
} from 'utils/iterationUtils.ts';
import useFetchSummaryReport from 'queries/fetch/fetchSummaryReport.ts';
import useFetchSpecificCampaign from 'queries/fetch/fetchSpecificCampaign.ts';
import useUser from 'hooks/useUser.ts';
import { getIsCurrentSummaryReport } from 'utils/summaryReports.ts';
import SummaryReportWarning from 'pages/campaign/iteration/[iterationId]/SummaryReports/SummaryReportWarning.tsx';
import { ISummaryReportLevelParams } from 'utils/parameterUtils.ts';

function SummaryReportPage() {
  const {
    teamId, campaignId, iterationId, batchId,
  } = useParams() as ISummaryReportLevelParams;
  const fetchSpecificCampaign = useFetchSpecificCampaign({
    teamId: teamId!,
    campaignId: campaignId!,
  });
  const fetchSummaryReports = useFetchSummaryReport({
    teamId: teamId!,
    campaignId: campaignId!,
    iterationId: iterationId!,
  });
  const user = useUser();

  if (
    !fetchSummaryReports.data
    || !fetchSpecificCampaign.data
    || fetchSummaryReports.status !== 'success'
    || fetchSpecificCampaign.status !== 'success'
  ) {
    return <ProgressIndicator text="Loading summary report..." />;
  }

  const currentIteration = getIterationById(fetchSpecificCampaign.data!, iterationId!);
  const currentIterationDetails = getIterationDetailsById(fetchSpecificCampaign.data!, iterationId!);
  const reportIndex = fetchSummaryReports.data?.findIndex((r) => r.batch_id === batchId);
  const report = fetchSummaryReports.data[reportIndex];
  const isCurrentSummaryReport = getIsCurrentSummaryReport(
    report,
    currentIteration!,
  );
  const isLatestSummaryReport = reportIndex === fetchSummaryReports.data.length - 1;

  if (!report) {
    throw new Error('Report not found.');
  }

  return (
    <>
      <div className="nhsuk-u-margin-bottom-6">
        <Back label="Summary reports" backTo={`/${teamId}/campaign/${campaignId}/iteration/${iterationId}/summary/reports`} />
      </div>
      <SummaryReportPageHeader
        title={`Report ${reportIndex + 1}`}
        iteration={currentIteration!}
        iterationDetails={currentIterationDetails!}
        report={report}
        summaryUrl={`/${teamId}/campaign/${campaignId}/iteration/${iterationId}/summary`}
        isLatestSummaryReport={isLatestSummaryReport}
      />
      <SummaryReportWarning
        report={report}
        currentIterationDetails={currentIterationDetails}
        isCurrentSummaryReport={isCurrentSummaryReport}
        user={user}
        teamId={teamId}
      />
      <ReportDetails
        report={report}
        iteration={currentIteration!}
        iterationDetails={currentIterationDetails!}
        teamId={teamId!}
        campaign={fetchSpecificCampaign.data!}
        isCurrentSummaryReport={isCurrentSummaryReport}
      />
    </>
  );
}

export default SummaryReportPage;
